import React, { Component } from 'react'
import { Button, message, Popover, Skeleton, Typography } from 'antd'
import { VerticalTableTransfer, TableColumn } from '../../../VerticalTableTransfer'
import { QuestionContextData } from '../../../../context/QuestionContext'
import IQuestionShelter from '../../../../interfaces/IQuestionShelter'
import IQuestion from '../../../../interfaces/IQuestion'
import ShelterQuestionAskAlias, { QuestionAskAlias } from '../../ShelterQuestionAskAlias'
import { ShelterKioskReasonUpdateQuestionRequest } from '../../../../services/api/interfaces/ApiRequest'

type ReasonQuestionProps = {
  shelterUuid: string,
  reasonUuid: string,
  context: QuestionContextData,

  loading: boolean,

  updateKioskQuestion: (args: ShelterKioskReasonUpdateQuestionRequest) => Promise<any>
}

export default class ReasonQuestion extends Component<ReasonQuestionProps> {

  private tableTransferColumns: TableColumn = {
    source: [
      {
        title: 'Group',
        dataIndex: ['group', 'name'],
        render: title => (
          <Typography.Text strong style={{fontSize: 12}}>{title}</Typography.Text>
        )
      },
      {
        dataIndex: 'ask',
        title: 'Question',
        searchable: true
      }
    ],
    target: [
      {
        title: 'Group',
        dataIndex: ['group', 'name'],
        render: title => (
          <Typography.Text strong style={{fontSize: 12}}>{title}</Typography.Text>
        )
      },
      {
        dataIndex: 'ask',
        title: 'Question',
        searchable: true
      },
      {
        dataIndex: 'ask_alias',
        title: 'Question Alias',
        render: (text: any, record: IQuestion) => {
          const question = this.getSingleQuestionShelter(record.uuid)
          if (!question) return null
          if (!question.question_uuid) return null

          const questionUuid: string = question.question_uuid
          const askAlias: string = question.ask_alias || ''

          return (
            <Popover
              title="Question Alias"
              content={(
                <ShelterQuestionAskAlias
                  question={question}
                  questionAskAlias={{
                    ask_alias: askAlias,
                    translation: question.translation?.map(v => {
                      return {
                        ask_alias: v.ask_alias,
                        language_id: v.language_id,
                        language: v.language
                      }
                    }) || []
                  }}
                  onSave={this.handleSetAskAlias}
                />
              )}
              placement="left"
              trigger="click"
              visible={this.state.askAliasPopover[questionUuid]}
              onVisibleChange={(visible: boolean) => this.handleAskAliasPopoverChange(questionUuid, visible)}
            >
              <Typography.Link>
                {question.ask_alias || 'Set question alias'}
              </Typography.Link>
            </Popover>
          )
        }
      }
    ]
  }

  state = {
    loaded: false,
    askAliasPopover: {} as Record<any, any>,
    questionShelter: [] as IQuestionShelter[],
  }

  async componentDidMount() {
    const { shelterUuid, context } = this.props

    if (shelterUuid && !context.fetching) {
      await context.requestQuestionList({
        search: {
          deleted: false,
          kiosk: true,
          organization_question_shelters: {
            with: {
              kiosk_reason: {
                with: true
              },
              shelter: {
                with: true
              },
              translation: {
                with: true
              }
            }
          },
          group: {
            with: true
          }
        },
        options: {
          _force_ids: true,
          limit: 9999
        }
      })

      const questionShelter = await this.getAllQuestionShelter()

      this.setState({
        loaded: true,
        questionShelter
      })
    }
  }

  private updateShelterQuestion = (updated: IQuestionShelter) => {
    const { questionShelter } = this.state

    const index = questionShelter.findIndex(v => v.question_uuid === updated.question_uuid)
    if (index >= 0) {
      questionShelter[index] = updated
    } else {
      questionShelter.push(updated)
    }

    this.setState({
      questionShelter
    })
  }

  private getAllQuestionShelter = async () => {
    const { shelterUuid, reasonUuid, context: { questions } } = this.props
    if (!questions) return []

    const shelterQuestions: IQuestionShelter[] = []

    questions.forEach(v => {
      const filteredQuestions = v.organization_question_shelters?.filter(s => s.shelter?.uuid === shelterUuid && s.kiosk_reason?.uuid === reasonUuid).map(question => {
        return {
          ...question,
          question_uuid: v.uuid
        }
      }) || []
      
      shelterQuestions.push(...filteredQuestions)
    })

    return shelterQuestions
  }

  private getSingleQuestionShelter = (questionUuid: string) => {
    const { questionShelter } = this.state

    return questionShelter.find(v => v.question_uuid === questionUuid)
  }

  private handleAskAliasPopoverChange = (questionUuid: string, visible: boolean) => {
    this.setState((state: any) => ({
      ...state,
      askAliasPopover: {
        ...state.askAliasPopover,
        [questionUuid]: visible
      }
    }))
  }

  private handleSetAskAlias = (questionShelter: IQuestionShelter, questionAskAlias: QuestionAskAlias) => {
    questionShelter.ask_alias = questionAskAlias.ask_alias
    questionShelter.translation = questionAskAlias.translation?.map(v => {
      return {
        ask_alias: v.ask_alias,
        language_id: v.language_id,
      }
    }) || [] as any
    
    this.updateShelterQuestion(questionShelter)

    if (questionShelter.question_uuid) {
      this.handleAskAliasPopoverChange(questionShelter.question_uuid, false)
    }
  }

  private handleTableTransferChange = (selectedKey: any[]) => {
    const { context } = this.props
    const questionShelter = context.questions?.filter(v => selectedKey.includes(v.uuid)).map(v => {
      return {
        ask_alias: '',
        question_uuid: v.uuid
      }
    })

    this.setState({
      questionShelter
    })
  }

  private handleSave = async () => {
    const { shelterUuid, reasonUuid, updateKioskQuestion } = this.props
    const { questionShelter } = this.state

    try {
      const payload: ShelterKioskReasonUpdateQuestionRequest = {
        organization_shelter_uuid: shelterUuid,
        organization_kiosk_reason_uuid: reasonUuid,
        questions: questionShelter.map(v => {
          return {
            question_uuid: v.question_uuid || '',
            ask_alias: v.ask_alias,
            translation: v.translation || []
          }
        })
      }
  
      await updateKioskQuestion(payload)
      message.success('Kiosk reason questions saved successfully!')
    } catch (error) {
      message.error(`Failed to save kiosk questions. Reason: ${error}`)
    }
  }

  render() {
    const { context, loading } = this.props
    const { loaded, questionShelter } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    return (
      <div>
        <VerticalTableTransfer
          rowKey="uuid"
          columns={this.tableTransferColumns}
          dataSource={context.questions || []}
          intialSelectedKeys={questionShelter.map(v => v.question_uuid)}
          onChange={this.handleTableTransferChange}
        />

        <Button
          type="primary"
          size="large"
          onClick={this.handleSave}
          loading={loading}
          block
        >
          Save Questions
        </Button>
      </div>
    )
  }
}
